import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TopPrescriptions = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    secondary: '#0f172a',
    success: '#10b981',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  const categories = [
    { id: 'all', name: 'All Medications' },
    { id: 'diabetes', name: 'Diabetes' },
    { id: 'heart', name: 'Heart & Blood Pressure' },
    { id: 'cholesterol', name: 'Cholesterol' },
    { id: 'mental', name: 'Mental Health' },
    { id: 'pain', name: 'Pain Relief' },
    { id: 'thyroid', name: 'Thyroid' },
    { id: 'antibiotics', name: 'Antibiotics' }
  ];

  const medications = [
    // Diabetes
    { name: 'Metformin', generic: true, category: 'diabetes', avgSavings: '85%', description: 'Type 2 diabetes' },
    { name: 'Glipizide', generic: true, category: 'diabetes', avgSavings: '80%', description: 'Type 2 diabetes' },
    { name: 'Januvia', generic: false, category: 'diabetes', avgSavings: '40%', description: 'Type 2 diabetes' },
    
    // Heart & Blood Pressure
    { name: 'Lisinopril', generic: true, category: 'heart', avgSavings: '90%', description: 'High blood pressure, heart failure' },
    { name: 'Amlodipine', generic: true, category: 'heart', avgSavings: '88%', description: 'High blood pressure, chest pain' },
    { name: 'Losartan', generic: true, category: 'heart', avgSavings: '85%', description: 'High blood pressure' },
    { name: 'Metoprolol', generic: true, category: 'heart', avgSavings: '87%', description: 'High blood pressure, heart conditions' },
    { name: 'Hydrochlorothiazide', generic: true, category: 'heart', avgSavings: '92%', description: 'High blood pressure, fluid retention' },
    
    // Cholesterol
    { name: 'Atorvastatin', generic: true, category: 'cholesterol', avgSavings: '90%', description: 'High cholesterol (generic Lipitor)' },
    { name: 'Simvastatin', generic: true, category: 'cholesterol', avgSavings: '88%', description: 'High cholesterol' },
    { name: 'Rosuvastatin', generic: true, category: 'cholesterol', avgSavings: '75%', description: 'High cholesterol (generic Crestor)' },
    
    // Mental Health
    { name: 'Sertraline', generic: true, category: 'mental', avgSavings: '85%', description: 'Depression, anxiety (generic Zoloft)' },
    { name: 'Escitalopram', generic: true, category: 'mental', avgSavings: '82%', description: 'Depression, anxiety (generic Lexapro)' },
    { name: 'Fluoxetine', generic: true, category: 'mental', avgSavings: '90%', description: 'Depression (generic Prozac)' },
    { name: 'Bupropion', generic: true, category: 'mental', avgSavings: '78%', description: 'Depression, smoking cessation' },
    { name: 'Trazodone', generic: true, category: 'mental', avgSavings: '85%', description: 'Depression, insomnia' },
    
    // Pain Relief
    { name: 'Gabapentin', generic: true, category: 'pain', avgSavings: '80%', description: 'Nerve pain, seizures' },
    { name: 'Meloxicam', generic: true, category: 'pain', avgSavings: '88%', description: 'Pain, inflammation' },
    { name: 'Cyclobenzaprine', generic: true, category: 'pain', avgSavings: '85%', description: 'Muscle spasms' },
    { name: 'Naproxen', generic: true, category: 'pain', avgSavings: '75%', description: 'Pain, inflammation' },
    
    // Thyroid
    { name: 'Levothyroxine', generic: true, category: 'thyroid', avgSavings: '85%', description: 'Hypothyroidism' },
    { name: 'Synthroid', generic: false, category: 'thyroid', avgSavings: '50%', description: 'Hypothyroidism' },
    
    // Antibiotics
    { name: 'Amoxicillin', generic: true, category: 'antibiotics', avgSavings: '90%', description: 'Bacterial infections' },
    { name: 'Azithromycin', generic: true, category: 'antibiotics', avgSavings: '85%', description: 'Bacterial infections (Z-Pack)' },
    { name: 'Ciprofloxacin', generic: true, category: 'antibiotics', avgSavings: '88%', description: 'Bacterial infections' },
    { name: 'Doxycycline', generic: true, category: 'antibiotics', avgSavings: '75%', description: 'Bacterial infections, acne' }
  ];

  const filteredMedications = selectedCategory === 'all' 
    ? medications 
    : medications.filter(med => med.category === selectedCategory);

  const handleSearchDrug = (drugName) => {
    navigate(`/search?drug=${encodeURIComponent(drugName)}`);
  };

  return (
    <div style={{ 
      fontFamily: "'Inter', -apple-system, sans-serif",
      background: colors.bg,
      minHeight: '100vh'
    }}>
      {/* Hero Section - NO WHITE GAP */}
      <section style={{
        background: colors.primary,
        padding: isMobile ? '48px 16px' : '80px 24px',
        textAlign: 'center',
        marginTop: 0
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h1 style={{
            fontSize: isMobile ? 28 : 48,
            fontWeight: 800,
            color: 'white',
            marginBottom: 14,
            lineHeight: 1.2
          }}>
            Top Prescription Medications
          </h1>
          <p style={{
            fontSize: isMobile ? 15 : 20,
            color: 'rgba(255,255,255,0.85)',
            lineHeight: 1.6,
            maxWidth: 600,
            margin: '0 auto'
          }}>
            Find the best prices on the most commonly prescribed medications. 
            Save up to 90% with your free Rx.ChatWithDr discount card.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ padding: isMobile ? '32px 16px' : '60px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          
          {/* Category Filter */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: isMobile ? 8 : 10, 
            justifyContent: 'center', 
            marginBottom: isMobile ? 28 : 40 
          }}>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  padding: isMobile ? '10px 16px' : '12px 22px',
                  borderRadius: 25,
                  border: selectedCategory === cat.id ? 'none' : `2px solid ${colors.gray200}`,
                  background: selectedCategory === cat.id ? colors.primary : 'white',
                  color: selectedCategory === cat.id ? 'white' : colors.gray800,
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 15,
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Medications Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: isMobile ? 14 : 20
          }}>
            {filteredMedications.map((med, index) => (
              <div 
                key={index} 
                style={{
                  background: 'white',
                  borderRadius: 16,
                  padding: isMobile ? 20 : 24,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  border: `1px solid ${colors.gray200}`,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  }
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  marginBottom: 12 
                }}>
                  <div>
                    <div style={{ 
                      fontSize: isMobile ? 18 : 20, 
                      fontWeight: 600, 
                      color: colors.gray900, 
                      marginBottom: 6 
                    }}>
                      {med.name}
                    </div>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 600,
                      background: med.generic ? '#e8f5e9' : '#fff3e0',
                      color: med.generic ? '#2e7d32' : '#e65100'
                    }}>
                      {med.generic ? 'Generic' : 'Brand'}
                    </span>
                  </div>
                </div>
                
                <p style={{ 
                  fontSize: isMobile ? 14 : 15, 
                  color: colors.gray600, 
                  marginBottom: 16, 
                  lineHeight: 1.5 
                }}>
                  {med.description}
                </p>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  paddingTop: 16, 
                  borderTop: `1px solid ${colors.gray100}` 
                }}>
                  <div>
                    <span style={{ fontSize: 13, color: colors.gray500 }}>Avg. Savings:</span>
                    <span style={{ 
                      fontSize: isMobile ? 22 : 26, 
                      fontWeight: 700, 
                      color: colors.success, 
                      marginLeft: 8 
                    }}>
                      {med.avgSavings}
                    </span>
                  </div>
                  <button 
                    style={{
                      padding: isMobile ? '10px 18px' : '12px 22px',
                      background: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: isMobile ? 14 : 15,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSearchDrug(med.name)}
                  >
                    Find Prices
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Info Section */}
          <div style={{
            background: 'white',
            borderRadius: 16,
            padding: isMobile ? 28 : 40,
            marginTop: isMobile ? 40 : 60,
            textAlign: 'center',
            border: `1px solid ${colors.gray200}`
          }}>
            <h2 style={{ 
              fontSize: isMobile ? 20 : 26, 
              fontWeight: 600, 
              color: colors.gray900, 
              marginBottom: 16 
            }}>
              Why Generic Medications Save You More
            </h2>
            <p style={{ 
              color: colors.gray600, 
              lineHeight: 1.7, 
              maxWidth: 700, 
              margin: '0 auto',
              fontSize: isMobile ? 14 : 16
            }}>
              Generic medications contain the same active ingredients as brand-name drugs 
              and are held to the same FDA standards for safety and effectiveness. 
              The main difference? Generic drugs typically cost 80-90% less than their 
              brand-name counterparts. Ask your doctor if a generic option is right for you.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TopPrescriptions;